import { authenticatedRequest } from '@/services';
import { HttpMethod } from '@/lib/enums';
import type { User } from '@/lib/types';
import type {
  CheckEmailResponse,
  ConsumeImpersonationResponse,
  ForgotPasswordResponse,
  LoginResponse,
  LogoutResponse,
  RefreshResponse,
  ResendVerificationResponse,
  ResetPasswordResponse,
  SignupResponse,
  ValidateResetTokenResponse,
  VerifyResponse,
} from '../lib';

// --- Service functions ---

export const signupUser = async (data: {
  fullName: string;
  email: string;
  password: string;
}): Promise<SignupResponse> => {
  return authenticatedRequest({
    resource: 'auth/signup',
    method: HttpMethod.POST,
    data,
    isAuthenticated: false,
  });
};

export const loginUser = async (data: {
  email: string;
  password: string;
  rememberMe: boolean;
}): Promise<LoginResponse> => {
  return authenticatedRequest({
    resource: 'auth/login',
    method: HttpMethod.POST,
    data,
    isAuthenticated: false,
  });
};

export const checkEmail = async (email: string): Promise<CheckEmailResponse> => {
  return authenticatedRequest({
    resource: 'auth/check-email',
    method: HttpMethod.POST,
    data: { email },
    isAuthenticated: false,
  });
};

/**
 * How long the session check may take. The staging/prod gateway cuts a request at 15s, so
 * waiting longer only keeps the app on its loader with nothing on screen (NPJB-815).
 */
const ME_TIMEOUT_MS = 15_000;

export const getMe = async (): Promise<User> => {
  return authenticatedRequest({
    resource: 'auth/me',
    method: HttpMethod.GET,
    timeout: ME_TIMEOUT_MS,
  });
};

export const verifyEmail = async (token: string): Promise<VerifyResponse> => {
  return authenticatedRequest({
    resource: 'auth/verify',
    method: HttpMethod.GET,
    params: { token },
    isAuthenticated: false,
  });
};

export const resendVerification = async (email: string): Promise<ResendVerificationResponse> => {
  return authenticatedRequest({
    resource: 'auth/resend-verification',
    method: HttpMethod.POST,
    data: { email },
    isAuthenticated: false,
  });
};

export const forgotPassword = async (email: string): Promise<ForgotPasswordResponse> => {
  return authenticatedRequest({
    resource: 'auth/forgot-password',
    method: HttpMethod.POST,
    data: { email },
    isAuthenticated: false,
  });
};

export const validateResetToken = async (token: string): Promise<ValidateResetTokenResponse> => {
  return authenticatedRequest({
    resource: 'auth/reset-password/validate',
    method: HttpMethod.GET,
    params: { token },
    isAuthenticated: false,
  });
};

export const resetPassword = async (data: {
  token: string;
  newPassword: string;
}): Promise<ResetPasswordResponse> => {
  return authenticatedRequest({
    resource: 'auth/reset-password',
    method: HttpMethod.POST,
    data,
    isAuthenticated: false,
  });
};

export const refreshToken = async (): Promise<RefreshResponse> => {
  return authenticatedRequest({
    resource: 'auth/refresh',
    method: HttpMethod.POST,
    isAuthenticated: false,
  });
};

export const logout = async (): Promise<LogoutResponse> => {
  return authenticatedRequest({
    resource: 'auth/logout',
    method: HttpMethod.POST,
    isAuthenticated: false,
  });
};

export const consumeImpersonation = async (
  token: string,
): Promise<ConsumeImpersonationResponse> => {
  return authenticatedRequest({
    resource: 'auth/impersonate',
    method: HttpMethod.POST,
    data: { token },
    isAuthenticated: false,
  });
};
